import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Mail, MapPin, Phone, X } from "lucide-react";
import { useContent } from "@/content/ContentContext";
import { LangSwitcher, useLang } from "@/i18n/LanguageContext";

const menuOrder = [
  "hdpe-pipes",
  "sprinkler-systems",
  "mini-sprinklers",
  "micro-sprinklers",
  "portable-sprinklers",
  "drip-systems",
  "rain-guns",
  "fittings-valves",
];

const pageLinks = [
  { key: "menuAbout", to: "/about" },
  { key: "menuHistory", to: "/history" },
  { key: "menuLeadership", to: "/leadership" },
  { key: "menuSustainability", to: "/sustainability" },
  { key: "menuSegments", to: "/segments" },
  { key: "menuCropAdvisor", to: "/crop-advisor" },
  { key: "menuTeam", to: "/about#team" },
  { key: "navStories", to: "/stories" },
  { key: "navCrops", to: "/crops" },
  { key: "navUpdates", to: "/updates" },
  { key: "navContact", to: "/contact" },
];

export default function MenuOverlay({ open, onClose }) {
  const content = useContent();
  const { t } = useLang();
  const c = content.contact;
  const [productsOpen, setProductsOpen] = useState(false);
  const [fittingsOpen, setFittingsOpen] = useState(false);

  useEffect(() => {
    if (open) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open]);

  const ordered = menuOrder
    .map((id) => content.products.find((p) => p.id === id))
    .filter(Boolean)
    .concat(content.products.filter((p) => !menuOrder.includes(p.id)));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          data-testid="menu-overlay"
          data-lenis-prevent
          className="fixed inset-0 z-[90] overflow-y-auto overscroll-contain bg-forest/95 backdrop-blur-xl"
          style={{ WebkitOverflowScrolling: "touch", backgroundColor: "rgba(244, 248, 252, 0.97)" }}
        >
          <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-5 lg:px-12">
            <p className="font-display text-xl font-bold tracking-tight text-cream">
              JAMNA <span className="italic text-harvest">INDUSTRIES</span>
            </p>
            <button
              onClick={onClose}
              data-testid="menu-close-button"
              aria-label="Close menu"
              className="border border-line p-3 text-cream transition-colors duration-300 hover:border-harvest hover:text-harvest"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mx-auto grid max-w-[1440px] gap-14 px-6 pb-16 pt-10 lg:grid-cols-12 lg:px-12">
            <nav className="lg:col-span-8" data-testid="menu-nav">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
              >
                <Link
                  to="/"
                  onClick={onClose}
                  data-testid="menu-link-home"
                  className="group block py-5"
                >
                  <span className="font-display text-3xl font-medium tracking-tight text-cream transition-colors duration-300 group-hover:text-harvest sm:text-4xl">
                    {t("menuHome")}
                  </span>
                </Link>
              </motion.div>
              <button
                onClick={() => setProductsOpen((o) => !o)}
                data-testid="menu-products-toggle"
                className="group flex w-full items-center justify-between border-t border-line py-5 text-left"
              >
                <span className="font-display text-3xl font-medium tracking-tight text-cream transition-colors duration-300 group-hover:text-harvest sm:text-4xl">
                  {t("menuProducts")}
                </span>
                <ChevronDown
                  className={`h-6 w-6 text-harvest transition-transform duration-300 ${
                    productsOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              <AnimatePresence initial={false}>
                {productsOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                    data-testid="menu-products-dropdown"
                  >
                    <div className="grid gap-x-10 pb-6 sm:grid-cols-2">
                      {ordered.map((p, i) => (
                        <motion.div
                          key={p.id}
                          initial={{ opacity: 0, x: -16 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.04, duration: 0.4 }}
                        >
                          <Link
                            to={`/products/${p.id}`}
                            onClick={onClose}
                            data-testid={`menu-product-${p.id}`}
                            className="group/item flex items-center justify-between border-b border-line/60 py-3.5"
                          >
                            <span className="text-sm uppercase tracking-[0.15em] text-sage transition-colors duration-300 group-hover/item:text-harvest">
                              {p.name}
                            </span>
                            <span className="font-display text-xs italic text-harvest/60">
                              {p.num}
                            </span>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                onClick={() => setFittingsOpen((o) => !o)}
                data-testid="menu-fittings-toggle"
                className="group flex w-full items-center justify-between border-t border-line py-5 text-left"
              >
                <span className="font-display text-3xl font-medium tracking-tight text-cream transition-colors duration-300 group-hover:text-harvest sm:text-4xl">
                  {t("menuFittings")}
                </span>
                <ChevronDown
                  className={`h-6 w-6 text-harvest transition-transform duration-300 ${
                    fittingsOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              <AnimatePresence initial={false}>
                {fittingsOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                    data-testid="menu-fittings-dropdown"
                  >
                    <div className="grid gap-x-10 pb-6 sm:grid-cols-2">
                      {(content.fittings || []).map((f, i) => (
                        <motion.div
                          key={f.id}
                          initial={{ opacity: 0, x: -16 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.04, duration: 0.4 }}
                        >
                          <Link
                            to="/fittings"
                            onClick={onClose}
                            data-testid={`menu-fitting-${f.id}`}
                            className="group/item flex items-center justify-between border-b border-line/60 py-3.5"
                          >
                            <span className="text-sm uppercase tracking-[0.15em] text-sage transition-colors duration-300 group-hover/item:text-harvest">
                              {f.name}
                            </span>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {pageLinks.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.05, duration: 0.5 }}
                >
                  <Link
                    to={l.to}
                    onClick={onClose}
                    data-testid={`menu-link-${l.key.toLowerCase()}`}
                    className="group block border-t border-line py-5"
                  >
                    <span className="font-display text-3xl font-medium tracking-tight text-cream transition-colors duration-300 group-hover:text-harvest sm:text-4xl">
                      {t(l.key)}
                    </span>
                  </Link>
                </motion.div>
              ))}
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="lg:col-span-4"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-harvest">{t("contactUs")}</p>
              <div className="mt-6 space-y-5">
                <a
                  href={`tel:${c.phone.replace(/\s/g, "")}`}
                  data-testid="menu-phone-link"
                  className="flex items-center gap-3 text-cream transition-colors duration-300 hover:text-harvest"
                >
                  <Phone className="h-4 w-4 text-harvest" /> {c.phone}
                </a>
                <a
                  href={`mailto:${c.email}`}
                  data-testid="menu-email-link"
                  className="flex items-center gap-3 text-cream transition-colors duration-300 hover:text-harvest"
                >
                  <Mail className="h-4 w-4 text-harvest" /> {c.email}
                </a>
                <p className="flex items-start gap-3 text-sage">
                  <MapPin className="mt-1 h-4 w-4 shrink-0 text-harvest" /> {c.address}
                </p>
              </div>
              <Link
                to="/contact?interest=dealership"
                onClick={onClose}
                data-testid="menu-dealer-link"
                className="mt-10 inline-flex items-center gap-2 bg-harvest px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:bg-harvestHover"
              >
                {t("becomeDealer")}
              </Link>
              <p className="mt-12 font-display text-lg italic text-sage/70">
                {t("taglineLine")}
              </p>
              <LangSwitcher className="mt-8 w-max" />
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
